import { IProductData, TPathParams, getCompleteSelectionData } from '@/utils';
import { TCartItem } from '@/lib/cart/useCart';
import { deslugify, slugify } from '@/lib/utils';
import { ElevarProduct, ElevarUserProperties } from './types';
import { IS_PREVIEW } from '@/utils/env';

// Initialize Elevar data layer if it doesn't exist
export const initializeElevarDataLayer = (): void => {
  if (typeof window !== 'undefined') {
    window.ElevarDataLayer = window.ElevarDataLayer ?? [];
  }
};

// Get user properties for logged in vs guest users
export const getUserProperties = (): ElevarUserProperties => {
  // For now, return guest user properties
  // TODO: Integrate with your auth system when user data is available
  return {
    visitor_type: 'guest', // required: "logged_in" | "guest"
    // Add customer data when available:
    // customer_email: user?.email,
    // customer_first_name: user?.firstName,
    // customer_last_name: user?.lastName,
    // customer_id: user?.id,
    // etc.
    // customer_address_1: "1 Hills Plantation Drive",
    // customer_address_2: "",
    // customer_city: "Charleston",
    // customer_country: "United States",
    // customer_email: "bill@gmail.com",
    // customer_first_name: "Bill",
    // customer_id: "5928549548188",
    // customer_last_name: "Call",
    // customer_order_count: "1",
    // customer_phone: "999-999-9999",
    // customer_province: "South Carolina",
    // customer_province_code: "SC",
    // customer_tags: "",
    // customer_total_spent: "0.0",
    // customer_zip: "22222",
  };
};

// Remove make from display ID (same logic as Google Tag implementation)
const removeMakeFromDisplayId = (displayId: string, make: string): string => {
  if (displayId === null || make === null) {
    return displayId ?? '';
  }

  if (displayId?.includes(make)) {
    return displayId?.replace(make, '').trim();
  }

  return displayId;
};

// Build safe product name without undefined values
export const buildSafeProductName = (
  product: IProductData,
  params?: TPathParams | null,
  isComplete?: boolean
): string => {
  if (isComplete && product.fullProductName) {
    // Use existing fullProductName if complete and available
    const cleanedDisplayId = removeMakeFromDisplayId(product.display_id || '', product.make || '');
    return `${product.fullProductName} ${cleanedDisplayId} ${product.type || ''}`.trim();
  }

  // Construct safe name using same pattern as mapCartItemsToGTagItems
  const fullProductName = [
    product.make,
    product.model,
    product.year_generation,
    product.submodel1,
    product.submodel2,
    product.submodel3,
  ]
    .filter(Boolean) // Remove null/undefined values
    .join(' ')
    .trim();

  const cleanedDisplayId = removeMakeFromDisplayId(product.display_id || '', product.make || '');

  // Fallback to title if fullProductName is empty
  if (!fullProductName && product.title) {
    return `${product.title} ${cleanedDisplayId}`.trim();
  }

  return `${fullProductName} ${cleanedDisplayId} ${product.type || ''}`.trim();
};

// Extract numeric ID from Shopify GID format
export const extractShopifyId = (id: string): string => {
  if (!id) return '';

  // Check if it's a GID format: gid://shopify/Product/8534279127207
  const gidMatch = id.match(/gid:\/\/shopify\/\w+\/(\d+)/);
  if (gidMatch) {
    return gidMatch[1];
  }

  // Return as-is if it's already numeric or not GID format
  return id;
};

// Build proper product URL based on completion state and params
export const buildProductURL = (
  product: IProductData,
  params?: TPathParams | null,
  isComplete?: boolean
): string => {
  let make, model, year;
  const productType = product.type || '';

  if (params) {
    make = params.make;
    model = params.model;
    year = params.year;
  } else {
    make = product.make;
    model = product.model;
    year = product.year_generation;
  }

  // Determine cover type based on product type
  const determineCoverType = (type: string) => {
    const lowerCaseType = type.toLowerCase();
    if (lowerCaseType === 'seat covers') return 'leather';
    if (lowerCaseType === 'car covers') return 'premium-plus';
    if (lowerCaseType === 'floor mats') return 'textured';
    return 'premium-plus'; // default
  };

  const coverType = determineCoverType(product?.type || '');

  if (isComplete && make && model) {
    // Complete URL with all params
    const yearParam = year ? `?year=${year}` : '';
    return `/${slugify(productType || '')}/${coverType}/${slugify(make)}/${slugify(model)}`;
  }

  if (make && !model) {
    // Incomplete URL with just make
    const yearParam = year ? `?year=${year}` : '';
    return `/${slugify(productType || '')}/${coverType}/${slugify(make)}`;
  }

  // Basic URL structure
  return `/${slugify(productType || '')}/${coverType}`;
};

// Transform IProductData to ElevarProduct format
export const transformProductToElevar = (
  product: IProductData,
  params?: TPathParams | null,
  isComplete?: boolean,
  position?: number,
  list?: string
): ElevarProduct => {
  // Use safe product name builder to avoid undefined values
  const productName = buildSafeProductName(product, params, isComplete);

  // Build category string from your existing categorization
  const categoryParts = [
    deslugify(params?.productType || product?.type || ''),
    deslugify(params?.coverType || product?.display_id || ''),
    deslugify(params?.make || product?.make || ''),
    deslugify(params?.model || product?.model || ''),
  ].filter(Boolean);

  const category = categoryParts.join(',');

  // Build proper product URL
  const productURL = buildProductURL(product, params, isComplete);

  return {
    id: product.sku || '', // SKU
    name: productName,
    brand: 'Coverland',
    category,
    variant: product?.display_color || '',
    price: product.msrp?.toString() || '0',
    quantity: '1',
    position,
    list,
    product_id: extractShopifyId(product.productId || ''),
    variant_id: extractShopifyId(product.id || ''),
    compare_at_price: isComplete ? product.price?.toString() : undefined,
    image: product.feature || '',
    url: productURL || undefined,
  };
};

// Transform cart item to ElevarProduct format
export const transformCartItemToElevar = (
  cartItem: TCartItem,
  position?: number,
  list?: string
): ElevarProduct => {
  const fullProductName =
    `${cartItem.year_generation ?? ''} ${cartItem.make ?? ''} ${cartItem.model ?? ''} ${cartItem.submodel1 ?? ''} ${cartItem.submodel2 ?? ''} ${cartItem.submodel3 ?? ''}`.trim();

  const cleanedDisplayId = cartItem.display_id
    ? cartItem.display_id.replace(cartItem.make || '', '').trim()
    : '';

  const productName = `${fullProductName} ${cleanedDisplayId} ${cartItem.type}`;

  // Build category from cart item data
  const categoryParts = [cartItem.type, cleanedDisplayId, cartItem.make, cartItem.model].filter(
    Boolean
  );

  const category = categoryParts.join(',');

  return {
    id: cartItem.sku || '',
    name: productName,
    brand: 'Coverland',
    category,
    variant: cartItem.display_color || '',
    price: cartItem.msrp?.toString() || '0',
    quantity: cartItem.quantity?.toString() || '1',
    position,
    list: buildProductURL(cartItem, null, true) || undefined,
    product_id: extractShopifyId(cartItem.productId || ''),
    variant_id: extractShopifyId(cartItem.productVariantId || ''),
    compare_at_price: cartItem.msrp?.toString(),
    image: cartItem.feature || '',
    url: buildProductURL(cartItem, null, true) || undefined,
  };
};

// Transform multiple cart items to Elevar products
export const transformCartItemsToElevar = (
  cartItems: TCartItem[],
  list?: string
): ElevarProduct[] => {
  return cartItems.map((item, index) => transformCartItemToElevar(item, index + 1, list));
};

// Get current page type for context
export const getCurrentPageType = (): string => {
  if (typeof window === 'undefined') return 'unknown';

  const path = window.location.pathname;

  if (path === '/') return 'home';
  if (path.includes('/products/')) return 'product';
  if (path.includes('/collections/')) return 'collection';
  if (path.includes('/search')) return 'search';
  if (path.includes('/cart')) return 'cart';
  if (path.includes('/checkout')) return 'checkout';

  // Return 'pdp' for product pages matching car-covers/* and seat-covers/* patterns
  if (
    path.includes('/car-covers/') ||
    path.includes('/seat-covers/') ||
    path.includes('/floor-mats/')
  ) {
    return 'product';
  }

  return 'other';
};

// Get referrer list for tracking product journey
export const getReferrerList = (): string => {
  if (typeof window === 'undefined') return '';

  // Try to get from sessionStorage first (you might want to implement this)
  const storedReferrer = sessionStorage.getItem('product_referrer_list');
  if (storedReferrer) return storedReferrer;

  // Fallback to current pathname
  return window.location.pathname;
};

// Set referrer list for tracking product journey
export const setReferrerList = (listPath: string): void => {
  if (typeof window === 'undefined') return;

  sessionStorage.setItem('product_referrer_list', listPath);
};

// Clear referrer list
export const clearReferrerList = (): void => {
  if (typeof window === 'undefined') return;

  sessionStorage.removeItem('product_referrer_list');
};

// Push event to Elevar data layer safely
export const pushToElevarDataLayer = (event: any): void => {
  if (typeof window === 'undefined') return;

  initializeElevarDataLayer();

  try {
    window.ElevarDataLayer.push(event);
    if (IS_PREVIEW) {
      console.log('[Elevar] Event pushed:', event.event, event);
    }
  } catch (error) {
    console.error('[Elevar] Error pushing event:', error);
  }
};

// Invalidate Elevar context on route changes
export const invalidateElevarContext = (): void => {
  if (typeof window !== 'undefined' && window.ElevarInvalidateContext) {
    window.ElevarInvalidateContext();
  }
};

// Format price to string with 2 decimal places
export const formatPrice = (price: number | string | undefined): string => {
  if (!price) return '0.00';
  return Number(price).toFixed(2);
};
